'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Wrench, Settings, Zap, Shield, ChevronLeft, ChevronRight } from 'lucide-react';

const services = [
  {
    icon: Wrench,
    title: 'Reparación General',
    description:
      'Diagnóstico computarizado en minutos. Motores, frenos, transmisión y sistema eléctrico resueltos con garantía escrita. No más talleres que no encuentran el problema.',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80&auto=format&fit=crop',
    badge: 'Desde RD$500',
  },
  {
    icon: Settings,
    title: 'Mantenimiento Preventivo',
    description:
      'Cambio de aceite, filtros, bujías, ajuste de cadena y revisión de 21 puntos. El mantenimiento a tiempo te ahorra reparaciones costosas. Tu inversión protegida.',
    image:
      'https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?w=900&q=80&auto=format&fit=crop',
    badge: 'Tu garantía de confianza',
  },
  {
    icon: Zap,
    title: 'Personalización & Tunning',
    description:
      'Escapes deportivos, iluminación LED, suspensión ajustable y accesorios performance. Transforma tu moto en algo único. Diseño y potencia al gusto del piloto exigente.',
    image:
      'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=900&q=80&auto=format&fit=crop',
    badge: '+500 motos custom',
  },
  {
    icon: Shield,
    title: 'Repuestos Originales',
    description:
      'Stock completo de repuestos originales y piezas aftermarket certificadas para todas las marcas. Sin esperas, sin importaciones. Lo que necesitas, disponible hoy.',
    image:
      'https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=900&q=80&auto=format&fit=crop',
    badge: '+1,000 piezas en stock',
  },
];

export default function ServicesCarousel() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating || index === current) return;
      setIsAnimating(true);
      setCurrent(index);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsAnimating(false), 400);
    },
    [current, isAnimating],
  );

  const prev = useCallback(() => {
    goTo((current - 1 + services.length) % services.length);
  }, [current, goTo]);

  const next = useCallback(() => {
    goTo((current + 1) % services.length);
  }, [current, goTo]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const active = services[current];
  const IconComponent = active.icon;

  return (
    <div className="relative max-w-7xl mx-auto">
      {/* Main carousel card */}
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-[#111111] shadow-2xl">
        {/* Background image with dark overlay */}
        {services.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-center bg-cover transition-opacity duration-700"
            style={{
              backgroundImage: `url(${s.image})`,
              opacity: i === current ? 1 : 0,
              zIndex: 0,
            }}
            aria-hidden="true"
          />
        ))}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(10,10,10,0.90) 0%, rgba(10,10,10,0.75) 50%, rgba(10,10,10,0.55) 100%)',
            zIndex: 1,
          }}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="relative grid lg:grid-cols-2 gap-0 min-h-[420px]" style={{ zIndex: 2 }}>
          {/* Text side */}
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 bg-[#E31B23]/15 border border-[#E31B23]/30 rounded-full px-3 py-1 text-xs font-semibold text-[#FF4500] mb-5 w-fit">
              {active.badge}
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E31B23]/25 to-[#FF4500]/15 flex items-center justify-center shrink-0">
                <IconComponent className="w-6 h-6 text-[#FF4500]" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white">{active.title}</h3>
            </div>
            <p className="text-slate-300 leading-relaxed text-base sm:text-lg mb-8">
              {active.description}
            </p>
            <a
              href="https://wa.me/593995254062"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#E31B23] to-[#FF4500] hover:from-[#FF2D35] hover:to-[#FF6B35] text-white font-semibold transition-all w-fit shadow-lg shadow-[#E31B23]/25"
            >
              Agendar por WhatsApp
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Right side — thumbnail grid */}
          <div className="hidden lg:flex flex-col justify-center items-end p-8 gap-4">
            <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
              {services.map((s, i) => {
                const SIcon = s.icon;
                return (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`group relative overflow-hidden rounded-2xl aspect-square border-2 transition-all duration-300 cursor-pointer ${
                      i === current
                        ? 'border-[#E31B23] shadow-lg shadow-[#E31B23]/30 scale-105'
                        : 'border-white/10 opacity-60 hover:opacity-90 hover:border-white/30'
                    }`}
                  >
                    <div
                      className="absolute inset-0 bg-center bg-cover"
                      style={{ backgroundImage: `url(${s.image})` }}
                    />
                    <div className="absolute inset-0 bg-[#0a0a0a]/55 group-hover:bg-[#0a0a0a]/40 transition-all" />
                    <div className="relative h-full flex flex-col items-center justify-center p-2 z-10">
                      <SIcon className="w-5 h-5 text-white mb-1" />
                      <span className="text-white text-xs font-medium text-center leading-tight">
                        {s.title}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation row */}
      <div className="flex items-center justify-between mt-6 px-1">
        {/* Dot indicators */}
        <div className="flex gap-2">
          {services.map((s, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                i === current
                  ? 'w-6 h-2 bg-[#E31B23]'
                  : 'w-2 h-2 bg-white/25 hover:bg-white/50'
              }`}
              aria-label={`Servicio: ${s.title}`}
            />
          ))}
        </div>

        {/* Arrow controls */}
        <div className="flex gap-2">
          <button
            onClick={prev}
            className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 flex items-center justify-center text-white transition-all cursor-pointer"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="w-10 h-10 rounded-full border border-[#E31B23]/40 bg-[#E31B23]/10 hover:bg-[#E31B23]/25 flex items-center justify-center text-[#FF4500] transition-all cursor-pointer"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
